import React, { useContext, useState } from 'react';
import {
  Button,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink, // Alias to avoid lint warnings
  Typography,
} from '@saviynt/design-system';

import { FormLayout, WindowContext } from '../../../shared/components';
import { type PasswordConfig } from '../../../shared/types';
import { urls } from '../../../shared/utils';
import { changePassword } from '../data-sources';
import { type MultiMessageError, type ValidationResults } from '../types';
import { validatePassword } from '../utils';

import PasswordValidation from './PasswordValidation';

import './UpdatePasswordForm.css';

interface UpdatePasswordFormProps {
  oneTimeToken: string;
  passwordConfig: PasswordConfig;
  onSuccess: () => void;
}

/**
 * Form component with password verification and rule checking
 */
function UpdatePasswordForm({
  oneTimeToken,
  passwordConfig,
  onSuccess,
}: UpdatePasswordFormProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [passwordValidation, setPasswordValidation] = useState<
    ValidationResults | undefined
  >(undefined);
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[] | null>(null);
  const { locationAssign } = useContext(WindowContext);

  const passwordType = showPassword ? 'text' : 'password';

  const hasValidationIssues = (passwordValidation?.fail.length ?? 0) > 0;
  const hasPasswordMismatch =
    !!confirmedPassword && confirmedPassword.localeCompare(password) !== 0;
  const canContinue =
    password &&
    confirmedPassword &&
    !hasValidationIssues &&
    !hasPasswordMismatch;

  const handleUpdatePassword = (newPassword: string) => {
    setPassword(newPassword);
    setPasswordValidation(validatePassword(newPassword, passwordConfig));
  };

  const handleSubmit = (fe: React.FormEvent) => {
    fe.preventDefault();

    if (canContinue) {
      setIsLoading(true);

      changePassword({
        oneTimeToken,
        password,
      })
        .then(() => {
          onSuccess();
        })
        .catch((mmError: MultiMessageError) => {
          if (mmError.messages) {
            setErrors(mmError.messages);
          } else {
            setErrors([mmError.message]);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const footer = (
    <>
      <Button
        type='submit'
        kind='filled'
        isDisabled={!canContinue}
        isLoading={isLoading}
        className='Auth_UpdatePasswordForm-continueButton'>
        Continue
      </Button>
      <DSLink
        kind='button'
        text='Back to Login'
        className='Auth_UpdatePasswordForm-returnToLogin'
        onClick={() => locationAssign(urls.Login)}
      />
    </>
  );

  const error = errors && (
    <InlineMessage
      kind='background'
      text='Color Theme Critical'
      colorTheme='critical'
      leftIcon={<Icon kind='AlertCriticalFilled' />}>
      <>
        <Typography kind='label'>Login failed</Typography>
        {errors.map((err) => (
          <Typography kind='body2' key={err}>
            {err}
          </Typography>
        ))}
      </>
    </InlineMessage>
  );

  return (
    <FormLayout
      onSubmit={handleSubmit}
      footerContent={footer}
      errorContent={error}
      title='Reset your password'>
      <InputField
        name='password'
        placeholder='Enter New Password'
        type={passwordType}
        value={password}
        suffixButtonType='password'
        suffixOnClick={() => setShowPassword(!showPassword)}
        setValue={handleUpdatePassword}
        isCritical={hasValidationIssues}
        CriticalHelperText={
          <PasswordValidation
            results={passwordValidation}
            config={passwordConfig}
          />
        }
      />
      <InputField
        name='confirmPassword'
        placeholder='Re-Enter New Password'
        type={passwordType}
        value={confirmedPassword}
        suffixButtonType='password'
        suffixOnClick={() => setShowPassword(!showPassword)}
        setValue={setConfirmedPassword}
        isCritical={hasPasswordMismatch}
        CriticalHelperText={
          <InlineMessage
            text='Passwords do not match'
            colorTheme='critical'
            size='medium'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        }
      />
    </FormLayout>
  );
}

export default UpdatePasswordForm;
